import React, { useRef, useState } from "react";
import { Upload, message } from "antd";
import { PictureOutlined, DownloadOutlined, CameraOutlined } from "@ant-design/icons";
import { ModalItem, ButtonItem, InputItem, TextAreaItem, HeaderItem, SelectItem } from "../../../../Components/Items";
import comprimirImagen from "./comprimirImagen";
import useEsMovil from "./useEsMovil";
import AvisoHorario from "./AvisoHorario";
import { OPCIONES_TIPO_INCIDENCIA, MIN_DESCRIPCION, textoSuficiente } from "./fasesSoporte";

const MAX_IMAGENES = 3;
const TIPOS_VALIDOS = ["image/jpeg", "image/png", "image/webp", "image/heic", "image/heif"];

// Solo agrega el File al estado (con preview base64 local) — el envío real
// (multipart) ocurre hasta que el usuario da clic en "Reportar". Mismo patrón
// que EditarInsumo.js.
const getBase64 = (file) =>
  new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result);
  });

const ModalNuevoTicket = ({ visible, onCancel, onCrear, creando }) => {
  const esMovil = useEsMovil();
  const inputCamara = useRef(null);
  const [titulo, setTitulo] = useState("");
  const [tipoIncidencia, setTipoIncidencia] = useState(undefined);
  const [descripcion, setDescripcion] = useState("");
  const [pasosReproduccion, setPasosReproduccion] = useState("");
  const [archivos, setArchivos] = useState([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  const [previewTitle, setPreviewTitle] = useState("");

  const limpiarYCerrar = () => {
    setTitulo("");
    setTipoIncidencia(undefined);
    setDescripcion("");
    setPasosReproduccion("");
    setArchivos([]);
    onCancel();
  };

  const agregarArchivo = async (file, { uid } = {}) => {
    if (!TIPOS_VALIDOS.includes(file.type)) {
      message.info(`Para ${file.name} usa una imagen JPEG, PNG, WEBP o HEIC.`);
      return false;
    }

    const listo = await comprimirImagen(file, archivos.length);
    if (listo.size / 1024 / 1024 >= 5) {
      message.info(`${file.name} pesa más de 5 MB, prueba con una imagen más ligera.`);
      return false;
    }

    const preview = await getBase64(listo);
    setArchivos((prev) => [
      ...prev,
      { uid: uid || listo.name, name: listo.name, status: "done", thumbUrl: preview, preview, originFileObj: listo },
    ]);
    return true;
  };

  const customRequest = async (e) => {
    const ok = await agregarArchivo(e.file, { uid: e.file.uid });
    if (ok) e.onSuccess("ok");
    else e.onError("Imagen no válida");
  };

  const tomarFoto = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) agregarArchivo(file);
  };

  // Mismo patrón que EditarInsumo.js: clic en la miniatura abre un modal con la
  // imagen completa y opción de descarga, en vez del preview default de antd.
  const handlePreview = (file) => {
    setPreviewImage(file.preview || file.thumbUrl);
    setPreviewTitle(file.name);
    setPreviewVisible(true);
  };

  const reportar = () => {
    if (!titulo.trim()) {
      message.info("Ponle un título al reporte antes de continuar.");
      return;
    }
    if (!tipoIncidencia) {
      message.info("Elige el tipo de incidencia antes de continuar.");
      return;
    }
    if (!textoSuficiente(descripcion, MIN_DESCRIPCION)) {
      message.info("Tu descripción es muy corta, cuéntanos un poco más.");
      return;
    }

    const fd = new FormData();
    fd.append("titulo", titulo.trim());
    fd.append("tipoIncidencia", tipoIncidencia);
    fd.append("descripcion", descripcion.trim());
    if (pasosReproduccion.trim()) fd.append("pasosReproduccion", pasosReproduccion.trim());
    fd.append("urlVista", window.location.href);
    archivos.forEach((archivo, index) => {
      fd.append(`imagen${index + 1}`, archivo.originFileObj);
    });

    onCrear(fd, limpiarYCerrar);
  };

  return (
    <ModalItem
      visible={visible}
      title="Levantar un ticket"
      onCancel={limpiarYCerrar}
      footer={null}
      destroyOnClose
      spinning={false}
      width={esMovil ? undefined : 480}
    >
      <AvisoHorario style={{ marginBottom: 12 }} />

      <InputItem
        placeholder="Título del reporte (ej. No puedo facturar una venta)"
        value={titulo}
        onChange={(e) => setTitulo(e.target.value)}
        style={{ marginBottom: 12 }}
      />

      <SelectItem
        placeholder="Tipo de incidencia"
        value={tipoIncidencia}
        options={OPCIONES_TIPO_INCIDENCIA}
        onChange={(value) => setTipoIncidencia(value)}
        style={{ width: "100%", marginBottom: 12 }}
      />

      <TextAreaItem
        placeholder="Describe con detalle lo que estás necesitando..."
        rows={4}
        value={descripcion}
        onChange={(e) => setDescripcion(e.target.value)}
        style={{ marginBottom: 12 }}
      />

      <TextAreaItem
        placeholder="Pasos para reproducirlo (opcional, pero ayuda mucho a soporte)"
        rows={2}
        value={pasosReproduccion}
        onChange={(e) => setPasosReproduccion(e.target.value)}
        style={{ marginBottom: 4 }}
      />

      <HeaderItem>ADJUNTAR IMÁGENES (OPCIONAL)</HeaderItem>

      <Upload
        listType="picture-card"
        fileList={archivos}
        customRequest={customRequest}
        onPreview={handlePreview}
        onRemove={(file) => setArchivos((prev) => prev.filter((a) => a.uid !== file.uid))}
        beforeUpload={() => archivos.length < MAX_IMAGENES}
        accept="image/*"
        multiple
      >
        {archivos.length >= MAX_IMAGENES ? null : (
          <div>
            <PictureOutlined />
            <div style={{ marginTop: 8 }}>Agregar</div>
          </div>
        )}
      </Upload>
      {esMovil && archivos.length < MAX_IMAGENES && (
        <div style={{ marginBottom: 8 }}>
          <input
            ref={inputCamara}
            type="file"
            accept="image/*"
            capture="environment"
            onChange={tomarFoto}
            style={{ display: "none" }}
          />
          <ButtonItem naranjaB icon={<CameraOutlined />} onClick={() => inputCamara.current?.click()}>
            Tomar foto
          </ButtonItem>
        </div>
      )}
      <div style={{ fontSize: 11, color: "#8c8c8c", marginBottom: 12 }}>
        Hasta {MAX_IMAGENES} imágenes. Las fotos se reducen automáticamente antes de enviarse.
      </div>

      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <ButtonItem type="primary" naranja loading={creando} onClick={reportar}>
          Reportar
        </ButtonItem>
      </div>

      <ModalItem
        visible={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={() => setPreviewVisible(false)}
        spinning={false}
        width={esMovil ? undefined : 450}
      >
        <img alt="vista previa" style={{ width: "100%" }} src={previewImage} />
        <div style={{ textAlign: "center", marginTop: 16 }}>
          <a href={previewImage} download={previewTitle || "imagen"} style={{ textDecoration: "none" }}>
            <ButtonItem type="primary" icon={<DownloadOutlined />}>
              Descargar
            </ButtonItem>
          </a>
        </div>
      </ModalItem>
    </ModalItem>
  );
};

export default ModalNuevoTicket;
